import axios, { AxiosError, AxiosInstance } from "axios";
import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { ensureValidAuth, getAuthState, clearAuth } from "./auth";

// Get API client with auth headers
export function getApiClient(): AxiosInstance {
  const { apiKey, baseUrl, apiPath, isInitialized } =
    useSDKConfigStore.getState();

  if (!isInitialized || !apiKey) {
    throw new Error("SwypeSDK not initialized. Call initializeSwypeSDK first.");
  }

  const authState = getAuthState();
  if (!authState.signature || !authState.deadline) {
    throw new Error(
      "SwypeSDK auth not ready. Authentication signature missing."
    );
  }

  const fullBaseURL = `${baseUrl}${apiPath}`;

  const client = axios.create({
    baseURL: fullBaseURL,
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      Authorization: `Bearer ${authState.signature}`,
      "X-Deadline": authState.deadline.toString(),
    },
  });

  // Add request interceptor to ensure valid auth before each request
  client.interceptors.request.use(
    async (config) => {
      await ensureValidAuth();

      // Update headers with fresh auth if regenerated
      const freshAuthState = getAuthState();
      if (freshAuthState.signature && freshAuthState.deadline) {
        config.headers.set(
          "Authorization",
          `Bearer ${freshAuthState.signature}`
        );
        config.headers.set("X-Deadline", freshAuthState.deadline.toString());
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor for handling errors
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response) {
        console.error("API Error Response:", {
          status: error.response.status,
          data: error.response.data,
        });

        // If auth error, clear auth state to force regeneration
        if (error.response.status === 401 || error.response.status === 403) {
          clearAuth();
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
}
